import { useEffect, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getGradeBand } from '../../../shared/constant/grading';
import { TERMS } from '../../../shared/constant/terms';
import { showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { getSubjectGrades } from '../services/gradesService';
import { exportEClassRecord } from '../utils/eClassRecordExport';
import { exportGradeSummary } from '../utils/gradeSummaryExport';

const formatPercent = (value) => (value == null ? '—' : `${Number(value).toFixed(2)}%`);

function GradesSummaryPanel({ subject, sections, roster, rosterLoading, initialTerm, initialSection }) {
  const [term, setTerm] = useState(() =>
    TERMS.map(String).includes(initialTerm) ? initialTerm : String(TERMS[0])
  );
  const [section, setSection] = useState(() => (sections.includes(initialSection) ? initialSection : ''));
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    getSubjectGrades(subject.id, Number(term))
      .then((rows) => {
        if (mounted) setGrades(Object.fromEntries(rows.map((r) => [r.student_id, r])));
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load grades');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [subject.id, term]);

  // Which export is running: 'summary' or 'record'.
  const [exporting, setExporting] = useState('');

  const visible = section ? roster.filter((s) => s.section === section) : roster;

  // The E-Class forms are per section, so one has to be picked (or be the only one).
  const exportSection = section || (sections.length === 1 ? sections[0] : '');

  const runExport = async (kind, exportForm, doneMessage) => {
    if (!exportSection) return;
    setExporting(kind);
    try {
      const students = roster.filter((s) => s.section === exportSection);
      const leftOut = await exportForm(students);
      if (leftOut.length > 0) {
        showAlert({
          title: 'Exported, with students left out',
          text: `These students have no sex recorded, so they aren't on the Male or Female list: ${leftOut
            .map((s) => `${s.lastname}, ${s.firstname}`)
            .join('; ')}. Set it in Students and export again.`,
          icon: 'warning',
        });
      } else {
        showSuccessToast(doneMessage);
      }
    } catch (err) {
      showAlert({ title: 'Export failed', text: err.message || 'Failed to export' });
    } finally {
      setExporting('');
    }
  };

  const handleExportSummary = () =>
    runExport('summary', (students) => exportGradeSummary({ subject, section: exportSection, students }), 'Grade summary exported');

  const handleExportRecord = () =>
    runExport(
      'record',
      (students) => exportEClassRecord({ subject, section: exportSection, term: Number(term), students }),
      `E-Class Record for Term ${term} exported`
    );

  return (
    <div className="flex-1 min-h-0 flex flex-col space-y-3">
      <div className="shrink-0 space-y-2">
        <div className="flex gap-2">
          <select
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            aria-label="Term"
            className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          >
            {TERMS.map((t) => (
              <option key={t} value={t}>Term {t}</option>
            ))}
          </select>

          {sections.length > 0 && (
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              aria-label="Filter by section"
              className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              <option value="">All Sections</option>
              {sections.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={handleExportRecord}
            disabled={!exportSection || !!exporting || rosterLoading}
            title={
              exportSection
                ? `Export the Term ${term} E-Class Record for ${exportSection}`
                : 'Choose a section to export'
            }
            className="shrink-0 sm:ml-auto flex items-center justify-center gap-1.5 rounded-md bg-[#0b4a71] px-3 py-2 lg:text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
          >
            <ArrowDownTrayIcon className={`h-4 w-4 ${exporting === 'record' ? 'animate-pulse' : ''}`} />
            <span className="hidden sm:inline">{exporting === 'record' ? 'Exporting…' : 'Export E-Class'}</span>
            <span className="sr-only sm:hidden">Export E-Class</span>
          </button>

          <button
            type="button"
            onClick={handleExportSummary}
            disabled={!exportSection || !!exporting || rosterLoading}
            title={exportSection ? `Export the E-Class Summary for ${exportSection}` : 'Choose a section to export'}
            className="shrink-0 flex items-center justify-center gap-1.5 rounded-md bg-[#0b4a71] px-3 py-2 lg:text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
          >
            <ArrowDownTrayIcon className={`h-4 w-4 ${exporting === 'summary' ? 'animate-pulse' : ''}`} />
            <span className="hidden sm:inline">{exporting === 'summary' ? 'Exporting…' : 'Export Summary'}</span>
            <span className="sr-only sm:hidden">Export Summary</span>
          </button>
        </div>

        {sections.length > 1 && !section && (
          <p className="text-xs text-gray-500">Choose a section to export its E-Class Record or Summary.</p>
        )}

        <p className="text-xs text-gray-500">
          Weights: Attendance {Number(subject.att_weight)}% · Written Works {Number(subject.ww_weight)}% ·
          Performance Task {Number(subject.pt_weight)}% · Examination {Number(subject.exam_weight)}%. A term grade appears
          once the student has a score in all three components{Number(subject.att_weight) > 0 ? ' and attendance recorded' : ''}.
          Attendance counts present days only; late is not counted as attended and excused days are left out.
        </p>
      </div>

      {error && (
        <p className="shrink-0 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
      )}

      <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
        {rosterLoading || loading ? (
          <p className="text-center text-gray-500 py-12">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-gray-500 py-12">
            {sections.length === 0
              ? 'No sections are assigned to this subject yet. Ask an admin to assign them in Subjects.'
              : 'No students found.'}
          </p>
        ) : (
          <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 z-20 bg-[#4ea6d0] px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Section</th>
                  <th className="px-4 py-3 font-medium">Attendance</th>
                  <th className="px-4 py-3 font-medium">Written Works</th>
                  <th className="px-4 py-3 font-medium">Performance Task</th>
                  <th className="px-4 py-3 font-medium">Examination</th>
                  <th className="px-4 py-3 font-medium">Initial Grade</th>
                  <th className="px-4 py-3 font-medium">Term Grade</th>
                  <th className="px-4 py-3 font-medium">Letter</th>
                  <th className="px-4 py-3 font-medium">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((student) => {
                  const g = grades[student.id];
                  const termGrade = g?.term_grade ?? null;
                  const band = getGradeBand(termGrade);
                  return (
                    <tr key={student.id} className="group hover:bg-[#4ea6d0]/20">
                      <td className="sticky left-0 z-[1] max-w-[9rem] sm:max-w-none bg-white group-hover:bg-[#dcedf6] shadow-[inset_-1px_0_0_#e5e7eb] px-4 py-3 font-medium text-[#0b4a71]">
                        {student.lastname}, {student.firstname} {student.middlename ? `${student.middlename} ` : ''}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{student.section || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{formatPercent(g?.attendance_pct)}</td>
                      <td className="px-4 py-3 text-gray-600">{formatPercent(g?.written_work_pct)}</td>
                      <td className="px-4 py-3 text-gray-600">{formatPercent(g?.performance_task_pct)}</td>
                      <td className="px-4 py-3 text-gray-600">{formatPercent(g?.examination_pct)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {g?.initial_grade == null ? '—' : Number(g.initial_grade).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 font-medium text-[#0b4a71]">{termGrade ?? '—'}</td>
                      <td className="px-4 py-3 font-medium text-[#0b4a71]">{band?.letter ?? '—'}</td>
                      <td className="px-4 py-3">
                        {band ? (
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${band.tone}`}>
                            {band.remark}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default GradesSummaryPanel;
